import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="combobox" — ports the mockup's Combobox.tsx:
// types like a text input, filters its option list as you type, but still
// submits a plain id via a hidden input, so it drops into a normal Rails
// form with no server-side changes.
//
// A parent cascading controller (see customer_filter/project_filter) can
// restrict which options are selectable by calling #setConstraint via
// application.getControllerForElementAndIdentifier — see those controllers
// for the other half of this contract.
//
// Set data-combobox-free-text-value="true" for a suggestions-only field with
// no fixed set of valid values (e.g. the material line's "Unit") — there's
// no hidden target at all, the visible input is the real submitted field:
// picking a suggestion just fills it in, and it's never reset/cleared on
// focus or blur, so freely typed text (not just a listed suggestion) is
// preserved. See _material_line_fields.html.erb for the markup half.
export default class extends Controller {
  static targets = ["input", "hidden", "list", "option", "empty", "newOption", "newName"]
  static values = { freeText: Boolean, newEnabled: Boolean, newLabel: String }

  connect() {
    this.constraintAttribute = null
    this.constraintValue = null
    this.highlightedIndex = -1
    this.query = this.hasNewNameTarget ? this.newNameTarget.value : ""
    this.isOpen = false
    this.listTarget.hidden = true
    this.applyVisibility()

    this.outsideClickHandler = (event) => {
      if (!this.element.contains(event.target)) this.close()
    }
    document.addEventListener("mousedown", this.outsideClickHandler)

    // Stimulus doesn't guarantee an ancestor's controller connects after a
    // descendant's — a parent cascading controller (customer-filter/
    // project-filter) can't safely reach in during its own connect(), since
    // this combobox might not exist yet at that point. Announcing readiness
    // here instead lets them react once this specific combobox is actually
    // available, regardless of connect order.
    this.element.dispatchEvent(new Event("combobox:connect", { bubbles: true }))
  }

  disconnect() {
    document.removeEventListener("mousedown", this.outsideClickHandler)
  }

  // Public API for a parent cascading controller. `disabled` is passed in
  // explicitly (rather than inferred from `value === null`) since "no
  // constraint at all" and "constrained to nothing selected yet" are both
  // legitimate states with different meaning.
  setConstraint(attribute, value, disabled) {
    this.constraintAttribute = attribute
    this.constraintValue = value
    this.inputTarget.disabled = disabled
    if (disabled) this.close()

    const selected = this.selectedOption()
    if (selected && !this.optionMatchesConstraint(selected)) this.selectOption(null)

    this.applyVisibility()
  }

  focusInput() {
    if (this.inputTarget.disabled) return

    // A picker (Material/Customer/Project) clears on focus so re-opening
    // always starts a fresh search over the full list. A free-text field
    // keeps whatever's already typed — focusing it to edit shouldn't wipe it.
    if (!this.freeTextValue) {
      this.query = ""
      this.inputTarget.value = ""
    }
    this.open()
  }

  typed() {
    this.query = this.inputTarget.value
    this.open()
    this.applyVisibility()
  }

  navigate(event) {
    if (!this.isOpen) {
      if (event.key === "ArrowDown" || event.key === "Enter") {
        event.preventDefault()
        this.open()
      }
      return
    }

    const visible = this.visibleOptions()

    if (event.key === "ArrowDown") {
      event.preventDefault()
      this.highlightedIndex = Math.min(this.highlightedIndex + 1, visible.length - 1)
      this.highlight()
    } else if (event.key === "ArrowUp") {
      event.preventDefault()
      this.highlightedIndex = Math.max(this.highlightedIndex - 1, 0)
      this.highlight()
    } else if (event.key === "Enter") {
      event.preventDefault()
      const option = visible[this.highlightedIndex]
      if (option) {
        this.selectOption(option)
        this.close()
      }
    } else if (event.key === "Escape") {
      this.close()
    }
  }

  pick(event) {
    this.selectOption(event.currentTarget)
    this.close()
  }

  hoverHighlight(event) {
    this.highlightedIndex = this.visibleOptions().indexOf(event.currentTarget)
    this.highlight()
  }

  open() {
    if (this.inputTarget.disabled) return

    this.isOpen = true
    this.listTarget.hidden = false
    this.inputTarget.setAttribute("aria-expanded", "true")
    this.applyVisibility()
  }

  close() {
    this.isOpen = false
    this.listTarget.hidden = true
    this.inputTarget.setAttribute("aria-expanded", "false")
    this.query = ""
    // A picker snaps back to its last real selection (or blank) on close,
    // since only a listed option is ever a valid value. Free text has no
    // such constraint — leave whatever the visible input already holds.
    if (this.freeTextValue) return

    const selected = this.selectedOption()
    this.inputTarget.value = selected ? selected.dataset.label : ""
  }

  selectOption(option) {
    // The label goes in first: a listener on the change below (the steps of the
    // booking form) reads what is on screen, and would otherwise see it blank.
    this.inputTarget.value = option ? option.dataset.label : ""
    if (this.hasHiddenTarget) {
      this.hiddenTarget.value = option ? option.dataset.value : ""
      if (this.hasNewNameTarget) this.newNameTarget.value = option?.dataset.value === "__new__" ? this.query.trim() : ""
      // Nothing else observes a plain value assignment on a hidden input, so
      // dispatch a change event explicitly for chained cascading controllers.
      this.hiddenTarget.dispatchEvent(new Event("change", { bubbles: true }))
    }
  }

  selectedOption() {
    if (!this.hasHiddenTarget) return undefined

    return this.optionTargets.find((option) => option.dataset.value === this.hiddenTarget.value)
  }

  newSelectionName() {
    return this.hasNewNameTarget ? this.newNameTarget.value : ""
  }

  visibleOptions() {
    return this.optionTargets.filter((option) => !option.hidden)
  }

  // An option can opt out of constraint filtering entirely (e.g. "Unassigned
  // entry" is always selectable once the field is enabled, regardless of
  // which customer is chosen) via data-constraint-exempt="true".
  //
  // The constraint dataset value is space-separated (e.g. a customer linked
  // to several freelancers lists all their ids) so an option matches if the
  // current constraint value is any one of them.
  optionMatchesConstraint(option) {
    if (!this.constraintAttribute || option.dataset.constraintExempt === "true") return true

    const values = (option.dataset[this.constraintAttribute] || "").split(" ")
    return values.includes(this.constraintValue)
  }

  applyVisibility() {
    const query = this.query.trim().toLowerCase()
    let anyVisible = false

    for (const option of this.optionTargets) {
      if (this.hasNewOptionTarget && option === this.newOptionTarget) continue
      const matchesQuery = query === "" || option.dataset.label.toLowerCase().includes(query)
      const visible = matchesQuery && this.optionMatchesConstraint(option)
      option.hidden = !visible
      if (visible) anyVisible = true
    }

    if (this.hasNewOptionTarget) {
      const exactMatch = this.optionTargets.some((option) => option !== this.newOptionTarget && !option.hidden && option.dataset.label.trim().toLowerCase() === query)
      const showNew = this.newEnabledValue && query !== "" && !exactMatch
      this.newOptionTarget.hidden = !showNew
      if (showNew) {
        const name = this.query.trim()
        this.newOptionTarget.textContent = `+ ${this.newLabelValue}: ${name}`
        this.newOptionTarget.dataset.label = name
        anyVisible = true
      }
    }

    this.emptyTarget.hidden = anyVisible
    this.highlightedIndex = -1
    this.highlight()
  }

  highlight() {
    this.visibleOptions().forEach((option, index) => {
      const isHighlighted = index === this.highlightedIndex
      option.classList.toggle("bg-brand-50", isHighlighted)
      option.classList.toggle("text-brand-700", isHighlighted)
      option.classList.toggle("text-stone-700", !isHighlighted)
    })
  }
}
